import { ModelSet } from 'features/performance-report/model-set';
import { useGetCaseActionById } from 'pages/Cases/hooks/useGetCaseActionById';
import { useGetDisclosures } from 'features/performance-report/api';
import {
  filterPerformance,
  getPortfolioStatisticsRows,
  ModelSetsFilter,
  OptionFilter,
  portfolioStatisticsChartFilter,
  RiskProfileFilter,
} from 'pages/Cases/PortfolioConstruction/Summary/PerformanceReport';
import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  CoverSlideTemplate,
  Slides,
  TitleSlideTemplate,
} from '../../../slides';
import { usePostCalculatePerformance } from '../../api';
import { AnnualizedReturns } from '../annualized-returns';
import { AnnualizedReturnsTable } from '../annualized-returns-table';
import { CalendarYearReturns } from '../calendar-year-returns';
import { CalendarYearReturnsTable } from '../calendar-year-returns-table';
import { Disclosures } from '../disclosures';
import { GrowthSlide } from '../growth-slide';
import { GrowthSlideTable } from '../grow-slide-table';
import { ImportantInformation } from '../important-information';
import { RollingOneYearReturnAndExcessReturn } from '../rolling-one-year-return-and-excess-return';
import { RollingThreeYearReturnsAndExcessReturns } from '../rolling-three-year-returns-and-excess-returns/rolling-three-year-returns-and-excess-returns';
import { RollingThreeYearRiskAndSharpeRatio } from '../rolling-three-year-risk-and-sharpe-ratio';
import { ScatterPlots } from '../scatter-plots';
import { CircularProgress, Grid } from '@mui/material';
import { useGetCaseById } from 'pages/Cases/hooks/useGetCaseById';
import BackButton from 'components/BackButton';
import { SlideType } from 'features/slides/types';
import { CaseMilestoneAction } from 'pages/Cases/hooks/useGetCases';
import dayjs from 'dayjs';

const TODAYS_DATE = dayjs().format('YYYY-MM-DD');

interface CoverSlideState {
  title: string;
  subtitle1: string;
  subtitle2: string;
  date: Date;
  riskProfile: string;
}

type FilteredPerformance = any;

interface ModelSetTitleState {
  title: string;
}

export function PerformanceReportSlides(): React.ReactElement {
  const { actionId = '', caseId = '' } = useParams();
  const { caseData, isCaseLoading } = useGetCaseById({ id: caseId });
  const { action, isActionLoading, isActionFetching, getModelSetsBasicData } =
    useGetCaseActionById({ actionId, caseId });
  const { data: performance, isLoading: isPerformanceLoading } =
    usePostCalculatePerformance({
      enabled: !!action && !isActionLoading && !isActionFetching,
      performanceData: getModelSetsBasicData(),
    });
  const basicModelSets = getModelSetsBasicData();

  const [filter, setFilter] = React.useState({
    activeNames: basicModelSets.map(ms => ms.model_set_name),
    activeOptions: [1] as number[],
    activeRiskProfile: 3,
  });

  const shouldFetchDisclosures =
    !!action &&
    !isActionLoading &&
    !isActionFetching &&
    filter.activeNames.length > 0;

  const {
    data: disclosures,
    isLoading: isDisclosuresLoading,
    refetch: refetchDisclosures,
  } = useGetDisclosures({
    enabled: shouldFetchDisclosures,
    caseId,
    actions: action?.id,
    modelSetsNames: filter.activeNames,
    options: filter.activeOptions,
    riskProfile: filter.activeRiskProfile,
  });
  const [coverState, setCoverState] = React.useState<CoverSlideState>({
    title: 'Risk and Return Performance Report',
    subtitle1: 'William K Hall, Inc',
    subtitle2: 'Investment Portfolio Performance Report',
    date: new Date(),
    riskProfile: '3',
  });
  const [performanceChartsState, setPerformanceChartsState] =
    React.useState<ModelSetTitleState>({ title: 'Performance Charts' });
  const [modelSetTitleState, setModelSetTitleState] =
    React.useState<ModelSetTitleState>({ title: 'Model Sets in Scope' });

  const filteredPerformance = React.useMemo(
    () =>
      performance && action
        ? filterPerformance(
            performance,
            filter.activeNames,
            filter.activeOptions,
            filter.activeRiskProfile,
          )
        : [],
    [
      performance,
      filter.activeNames,
      filter.activeOptions,
      filter.activeRiskProfile,
    ],
  );

  useEffect(() => {
    setPerformanceChartsState(prev => ({
      ...prev,
      title: 'Performance Charts',
    }));
    setCoverState(prev => ({
      ...prev,
      subtitle1: caseData?.name ?? '',
      riskProfile: filter.activeRiskProfile.toString(),
    }));
    if (shouldFetchDisclosures) {
      refetchDisclosures();
    }
  }, [
    filter.activeRiskProfile,
    filter.activeNames,
    caseData?.name,
    shouldFetchDisclosures,
  ]);

  useEffect(() => {
    if (filter.activeNames.length === 0 && basicModelSets.length > 0) {
      setFilter(f => ({
        ...f,
        activeNames: basicModelSets.map(ms => ms.model_set_name),
      }));
    }
  }, [basicModelSets, filter.activeNames.length]);

  const modelSets = React.useMemo(() => {
    const data = performance ?? [];
    return filter.activeNames.length
      ? filter.activeNames
      : data.map(({ model_set_name }) => model_set_name);
  }, [filter.activeNames, performance]);

  const showRowsTable = (name: string) =>
    getPortfolioStatisticsRows(
      portfolioStatisticsChartFilter({ filteredPerformance, name }),
    ).length > 2;

  const slides: SlideType[] = [
    getCoverSlide(coverState, setCoverState),
    {
      id: 'important-information',
      state: {},
      setState: () => {},
      render: () =>
        disclosures ? (
          <ImportantInformation
            porfoliosReviewed={disclosures.data.portfoliosReviewed}
            strategistPerformance={disclosures.data.strategistPerformance}
          />
        ) : null,
    },
    getDisclosuresSlide(),
    getModelSetTitleSlide(modelSetTitleState, setModelSetTitleState),
    ...modelSets.map(name =>
      getModelSetHoldingsSlide(name, action!, filter.activeOptions),
    ),
    getPerformanceChartsSlide(
      performanceChartsState,
      setPerformanceChartsState,
    ),
    {
      id: 'growth',
      state: {},
      setState: () => {},
      render: () => (
        <GrowthSlide
          filteredPerformance={filteredPerformance}
          startDate={performance?.[0].performance_metadata.start_date}
          endDate={performance?.[0].performance_metadata.end_date}
          rowsGrowthTable={!showRowsTable('performance_stats')}
        />
      ),
    },
    ...(showRowsTable('performance_stats')
      ? [
          {
            id: 'growth-table',
            state: {},
            setState: () => {},
            render: () => (
              <GrowthSlideTable
                filteredPerformance={filteredPerformance}
                startDate={performance?.[0].performance_metadata.start_date}
                endDate={performance?.[0].performance_metadata.end_date}
              />
            ),
          },
        ]
      : []),
    {
      id: 'annualized-returns',
      state: {},
      setState: () => {},
      render: () => (
        <AnnualizedReturns
          filteredPerformance={filteredPerformance}
          rowsAnnualizedReturnsTable={!showRowsTable('trailing_year_returns')}
        />
      ),
    },
    ...(showRowsTable('trailing_year_returns')
      ? [
          {
            id: 'annualized-returns-table',
            state: {},
            setState: () => {},
            render: () => (
              <AnnualizedReturnsTable
                filteredPerformance={filteredPerformance}
              />
            ),
          },
        ]
      : []),
    {
      id: 'calendar-year-returns',
      state: {},
      setState: () => {},
      render: () => (
        <CalendarYearReturns
          filteredPerformance={filteredPerformance}
          rowsCalendarYearReturnsTable={!showRowsTable('calendar_yr_returns')}
        />
      ),
    },
    ...(showRowsTable('calendar_yr_returns')
      ? [
          {
            id: 'calendar-year-returns-table',
            state: {},
            setState: () => {},
            render: () => (
              <CalendarYearReturnsTable
                filteredPerformance={filteredPerformance}
              />
            ),
          },
        ]
      : []),
    getScatterPlotsSlide(filteredPerformance),
    getRollingOneYearReturnSlide(filteredPerformance),
    getRollingThreeYearReturnsSlide(filteredPerformance),
    getRollingThreeYearRiskSlide(filteredPerformance),
  ];

  const isLoadingAll =
    isCaseLoading ||
    isActionLoading ||
    isActionFetching ||
    isPerformanceLoading ||
    isDisclosuresLoading;

  if (isLoadingAll) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ height: '100%' }}
      >
        <CircularProgress size={60} />
      </Grid>
    );
  }

  return (
    <Grid container height="100%" width="100%">
      <Grid
        container
        direction="column"
        justifyContent="space-between"
        height="48px"
      >
        <Grid item xs={6}>
          <BackButton name="Exit preview" />
        </Grid>
        <Grid item xs={6}>
          <Grid container direction="row" justifyContent="flex-end" gap={2}>
            <ModelSetsFilter
              performance={performance || []}
              activeNames={filter.activeNames}
              setFilter={setFilter}
            />

            <OptionFilter
              activeOptions={filter.activeOptions}
              setFilter={setFilter}
            />

            <RiskProfileFilter
              activeRiskProfile={filter.activeRiskProfile}
              setFilter={setFilter}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid
        item
        sx={{ overflow: 'auto', height: 'calc(100% - 48px)', width: '100%' }}
      >
        <Slides
          slides={slides}
          /**
           * Added as part of SAGE-2894 (and SAGE-2938)
           * (https://magnifi-dev.atlassian.net/browse/SAGE-2938 + https://magnifi-dev.atlassian.net/browse/SAGE-2894)
           *
           * The BE started to accept file_name as part of the Upload Performance Report endpoint.
           *
           * The same name would be used when the user downloads the performance report.
           *
           * Example:
           * Performance Report - P3 - 2025-07-25
           */
          title={`Performance Report - P${filter.activeRiskProfile} - ${TODAYS_DATE}`}
        />
      </Grid>
    </Grid>
  );
}

/**
 * Performance Report Slides
 * List of components that are used to render the performance report slides.
 *
 * 1. Cover Slide
 * 2. Important Information Slide
 * 3. Disclosures Slide
 * 4. Model Set Title Slide
 * 5. Model Set Holdings Slide
 * 6. Performance Charts Slide
 * 7. Growth Slide
 */

const getCoverSlide = (
  state: CoverSlideState,
  setState: React.Dispatch<React.SetStateAction<CoverSlideState>>,
): SlideType => {
  return {
    id: 'cover',
    state,
    setState,
    render: ({ state, setState, editable }) => (
      <CoverSlideTemplate
        state={state}
        setState={setState}
        editable={editable}
      />
    ),
    footer: false,
  };
};

const getDisclosuresSlide = (): SlideType => {
  return {
    id: 'disclosures',
    state: {},
    setState: () => {},
    render: () => <Disclosures />,
  };
};

const getModelSetTitleSlide = (
  state: ModelSetTitleState,
  setState: React.Dispatch<React.SetStateAction<ModelSetTitleState>>,
): SlideType => {
  return {
    id: 'model-set-title',
    state,
    setState,
    render: ({ state, setState, editable }) => (
      <TitleSlideTemplate
        state={state}
        setState={setState}
        editable={editable}
      />
    ),
  };
};
const getModelSetHoldingsSlide = (
  name: string,
  action: CaseMilestoneAction,
  options: number[],
): SlideType => {
  return {
    id: `model-set-holdings-${name}`,
    state: {},
    setState: () => {},
    render: () => <ModelSet name={name} action={action} options={options} />,
  };
};

const getPerformanceChartsSlide = (
  state: ModelSetTitleState,
  setState: React.Dispatch<React.SetStateAction<ModelSetTitleState>>,
): SlideType => {
  return {
    id: 'performance-charts',
    state,
    setState,
    render: ({ state, setState, editable }) => (
      <TitleSlideTemplate
        state={state}
        setState={setState}
        editable={editable}
      />
    ),
  };
};

const getScatterPlotsSlide = (
  filteredPerformance: FilteredPerformance[],
): SlideType => ({
  id: 'scatter-plots',
  state: {},
  setState: () => {},
  render: () => <ScatterPlots filteredPerformance={filteredPerformance} />,
});

const getRollingOneYearReturnSlide = (
  filteredPerformance: FilteredPerformance[],
): SlideType => ({
  id: 'rolling-one-year-return-and-excess-return',
  state: {},
  setState: () => {},
  render: () => (
    <RollingOneYearReturnAndExcessReturn
      filteredPerformance={filteredPerformance}
    />
  ),
});

const getRollingThreeYearReturnsSlide = (
  filteredPerformance: FilteredPerformance[],
): SlideType => ({
  id: 'rolling-three-year-returns-and-excess-returns',
  state: {},
  setState: () => {},
  render: () => (
    <RollingThreeYearReturnsAndExcessReturns
      filteredPerformance={filteredPerformance}
    />
  ),
});

const getRollingThreeYearRiskSlide = (
  filteredPerformance: FilteredPerformance[],
): SlideType => ({
  id: 'rolling-three-year-risk-and-sharpe-ratio',
  state: {},
  setState: () => {},
  render: () => (
    <RollingThreeYearRiskAndSharpeRatio
      filteredPerformance={filteredPerformance}
    />
  ),
});
